
import React, { useState, useEffect, useRef } from "react";
import { Control } from "react-hook-form";
import { MapPin } from "lucide-react";
import { WaitlistFormValues } from "../schema";
import { CountySelector } from "./location/CountySelector";
import { NursingHomeSelector } from "./location/NursingHomeSelector";

interface SectionProps {
  control: Control<WaitlistFormValues>;
  preselectedNursingHomeSlug?: string;
  preselectedCounty?: string;
}

export function LocationPreferencesSection({ 
  control,
  preselectedNursingHomeSlug,
  preselectedCounty
}: SectionProps) {
  const nursingHomeSectionRef = useRef<HTMLDivElement>(null);
  const [selectedCountySlugs, setSelectedCountySlugs] = useState<string[]>(
    preselectedCounty ? [preselectedCounty] : []
  );
  const [selectedNursingHomes, setSelectedNursingHomes] = useState<string[]>(
    preselectedNursingHomeSlug ? [preselectedNursingHomeSlug] : []
  );
  const [openToSuggestions, setOpenToSuggestions] = useState(false);

  // Set field value whenever selectedNursingHomes changes
  useEffect(() => {
    // Auto-select the nursing home when the page loads if we have a preselected slug
    if (preselectedNursingHomeSlug && selectedNursingHomes.length === 0) {
      setSelectedNursingHomes([preselectedNursingHomeSlug]);
      
      // Scroll the nursing home section into view with a slight delay to ensure rendering
      setTimeout(() => {
        nursingHomeSectionRef.current?.scrollIntoView({ 
          behavior: 'smooth',
          block: 'center'
        });
      }, 500);
    }
  }, [preselectedNursingHomeSlug]);

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-grandpal-beige">
      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <MapPin className="h-5 w-5 text-grandpal-green" />
        Location & Preferences
      </h3>
      
      <div className="space-y-4">
        <CountySelector 
          control={control} 
          selectedCountySlugs={selectedCountySlugs}
          setSelectedCountySlugs={setSelectedCountySlugs}
        />
        
        <div ref={nursingHomeSectionRef}>
          <NursingHomeSelector 
            control={control}
            selectedCountySlugs={selectedCountySlugs}
            selectedNursingHomes={selectedNursingHomes}
            setSelectedNursingHomes={setSelectedNursingHomes}
            openToSuggestions={openToSuggestions}
            setOpenToSuggestions={setOpenToSuggestions}
          />
        </div>
      </div>
    </div>
  );
}
